import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import {useAdminTheme} from '@/contexts/AdminThemeContext';import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';import {
  Users, Plus, Copy, Check, Edit2, Trash2, ExternalLink, X,
  DollarSign, TrendingUp, UserPlus, Eye, KeyRound, ChevronDown,
  ChevronUp, CreditCard, Clock, ToggleLeft, ToggleRight, Lock,
  FileText, FileSpreadsheet, Download, Link2, Banknote, CheckCircle2,
  XCircle, ShieldCheck, Settings2, AlertTriangle
} from 'lucide-react';
import {toast} from 'sonner';
export default function AdminAffiliatesPage() {
  const { colors, isDark } = useAdminTheme();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchAffiliates = async () => {
    try {
      const res = await axios.get(`${API}/admin/affiliates`, { withCredentials: true });
      setAffiliates(res.data);
    } catch (err) {
      toast.error('Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAffiliates(); }, []);

  const viewDetail = async (aff) => {
    setSelectedAffiliate(aff);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/affiliates/${aff.affiliate_id}`, { withCredentials: true });
      setDetailData(res.data);
    } catch {
      toast.error('Failed to load affiliate details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }} data-testid="admin-affiliates-title">Affiliates</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Manage affiliate partners and track their performance.</p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition hover:brightness-110"
            style={{ background: colors.accent }}
            data-testid="create-affiliate-btn"
          >
            <Plus className="h-4 w-4" /> Add Affiliate
          </button>
        </div>

        {/* Global Report Downloads */}
        {affiliates.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs" style={{ color: colors.textMuted }}>Export All:</span>
            <a href={`${API}/admin/affiliates-report/pdf`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition hover:bg-white/5"
              style={{ borderColor: colors.cardBorder, color: colors.textMuted }}
              data-testid="global-report-pdf-btn"
            >
              <FileText className="h-3 w-3" /> PDF Report
            </a>
            <a href={`${API}/admin/affiliates-report/csv`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition hover:bg-white/5"
              style={{ borderColor: colors.cardBorder, color: colors.textMuted }}
              data-testid="global-report-csv-btn"
            >
              <FileSpreadsheet className="h-3 w-3" /> CSV Export
            </a>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard colors={colors} icon={Users} label="Total Affiliates" value={affiliates.length} />
          <SummaryCard colors={colors} icon={UserPlus} label="Total Signups" value={affiliates.reduce((s, a) => s + (a.total_signups || 0), 0)} />
          <SummaryCard colors={colors} icon={CreditCard} label="Total Conversions" value={affiliates.reduce((s, a) => s + (a.total_conversions || 0), 0)} />
          <SummaryCard colors={colors} icon={DollarSign} label="Total Earnings" value={`$${affiliates.reduce((s, a) => s + (a.total_earnings || 0), 0).toFixed(2)}`} />
        </div>

        {/* Affiliates List */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: colors.cardBorder, background: colors.card }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-6 w-6 border-2 border-t-transparent rounded-full" style={{ borderColor: colors.accent }} />
            </div>
          ) : affiliates.length === 0 ? (
            <div className="text-center py-12" style={{ color: colors.textMuted }}>
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No affiliates yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: colors.cardBorder }}>
              {affiliates.map((aff) => (
                <AffiliateRow key={aff.affiliate_id} aff={aff} colors={colors} isDark={isDark}
                  onView={() => viewDetail(aff)} onRefresh={fetchAffiliates} />
              ))}
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreate && <CreateAffiliateModal colors={colors} isDark={isDark} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAffiliates(); }} />}

        {/* Detail Modal */}
        {selectedAffiliate && (
          <AffiliateDetailModal
            colors={colors} isDark={isDark}
            affiliate={selectedAffiliate}
            detail={detailData}
            loading={detailLoading}
            onClose={() => { setSelectedAffiliate(null); setDetailData(null); }}
            onRefresh={() => { fetchAffiliates(); viewDetail(selectedAffiliate); }}
            onListRefresh={fetchAffiliates}
          />
        )}

        {/* Reserved Words + Payout Requests */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ReservedWordsSection colors={colors} isDark={isDark} />
          <PayoutRequestsSection colors={colors} isDark={isDark} />
        </div>
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ colors, icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: colors.cardBorder, background: colors.card }}>
      <Icon className="h-4 w-4 mb-2" style={{ color: colors.accent }} />
      <p className="text-lg font-bold" style={{ color: colors.text }}>{value}</p>
      <p className="text-xs" style={{ color: colors.textMuted }}>{label}</p>
    </div>
  );
}

function AffiliateRow({ aff, colors, isDark, onView, onRefresh }) {
  const [copied, setCopied] = useState(false);

  const copyCode = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/?ref=${aff.ref_code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleActive = async (e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/admin/affiliates/${aff.affiliate_id}`, { is_active: !aff.is_active }, { withCredentials: true });
      toast.success(`Affiliate ${aff.is_active ? 'deactivated' : 'activated'}`);
      onRefresh();
    } catch {
      toast.error('Failed to update affiliate');
    }
  };

  const commTypes = { one_time: 'One-time', time_limited: 'Time-limited', perpetual: 'Perpetual' };

  return (
    <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-white/5 transition" onClick={onView}
      data-testid={`affiliate-row-${aff.affiliate_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate" style={{ color: colors.text }}>{aff.name}</p>
          {!aff.is_active && (
            <span className="text-[10px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded-full">Inactive</span>
          )}
        </div>
        <p className="text-xs truncate" style={{ color: colors.textMuted }}>{aff.email}</p>
      </div>
      <div className="hidden sm:flex items-center gap-4 text-xs" style={{ color: colors.textMuted }}>
        <span>{aff.total_signups || 0} signups</span>
        <span>{aff.total_conversions || 0} converts</span>
        <span className="font-medium" style={{ color: colors.text }}>${(aff.total_earnings || 0).toFixed(2)}</span>
      </div>
      <div className="flex items-center gap-2 text-xs" style={{ color: colors.textMuted }}>
        <span className="px-2 py-1 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
          {aff.commission_rate}% &middot; {commTypes[aff.commission_type] || aff.commission_type}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={copyCode} className="p-1.5 rounded-lg hover:bg-white/10 transition" title="Copy referral link"
          data-testid={`copy-ref-${aff.affiliate_id}`}>
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" style={{ color: colors.textMuted }} />}
        </button>
        <button onClick={toggleActive} className="p-1.5 rounded-lg hover:bg-white/10 transition" title={aff.is_active ? 'Deactivate' : 'Activate'}>
          {aff.is_active
            ? <ToggleRight className="h-4 w-4 text-emerald-400" />
            : <ToggleLeft className="h-4 w-4 text-slate-500" />
          }
        </button>
        <button onClick={(e) => { e.stopPropagation(); onView(); }} className="p-1.5 rounded-lg hover:bg-white/10 transition" title="View details">
          <Eye className="h-3.5 w-3.5" style={{ color: colors.textMuted }} />
        </button>
      </div>
    </div>
  );
}

function CreateAffiliateModal({ colors, isDark, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    commission_rate: 10, commission_type: 'perpetual', commission_duration_months: 12,
    custom_slug: '',
  });
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/affiliates`, form, { withCredentials: true });
      toast.success('Affiliate created successfully');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create affiliate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border p-6 shadow-2xl" style={{ borderColor: colors.cardBorder, background: colors.card }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold" style={{ color: colors.text }}>Add Affiliate</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 transition"><X className="h-4 w-4" style={{ color: colors.textMuted }} /></button>
        </div>
        <form onSubmit={handleCreate} className="space-y-4">
          <Field label="Name" colors={colors}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
              className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none focus:ring-1"
              style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text, '--tw-ring-color': colors.accent }}
              data-testid="aff-create-name"
            />
          </Field>
          <Field label="Email" colors={colors}>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
              className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none focus:ring-1"
              style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
              data-testid="aff-create-email"
            />
          </Field>
          <Field label="Password" colors={colors}>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={8}
              className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none focus:ring-1"
              style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
              placeholder="Min 8 characters"
              data-testid="aff-create-password"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Commission Rate (%)" colors={colors}>
              <input type="number" value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: parseFloat(e.target.value) || 0 }))}
                min="0" max="100" step="0.1"
                className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none focus:ring-1"
                style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                data-testid="aff-create-rate"
              />
            </Field>
            <Field label="Commission Type" colors={colors}>
              <select value={form.commission_type} onChange={e => setForm(f => ({ ...f, commission_type: e.target.value }))}
                className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none focus:ring-1"
                style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                data-testid="aff-create-type"
              >
                <option value="perpetual">Perpetual</option>
                <option value="one_time">One-time</option>
                <option value="time_limited">Time-limited</option>
              </select>
            </Field>
          </div>
          {form.commission_type === 'time_limited' && (
            <Field label="Duration (months)" colors={colors}>
              <input type="number" value={form.commission_duration_months} onChange={e => setForm(f => ({ ...f, commission_duration_months: parseInt(e.target.value) || 1 }))}
                min="1" max="120"
                className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none focus:ring-1"
                style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                data-testid="aff-create-duration"
              />
            </Field>
          )}
          <Field label="Custom Vanity Slug (optional)" colors={colors}>
            <div className="flex items-center rounded-xl border overflow-hidden" style={{ borderColor: colors.cardBorder, background: isDark ? 'rgba(255,255,255,0.05)' : '#fff' }}>
              <span className="text-[10px] pl-3 shrink-0" style={{ color: colors.textMuted }}>/api/r/</span>
              <input value={form.custom_slug} onChange={e => setForm(f => ({ ...f, custom_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                className="flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none font-mono"
                style={{ color: colors.text }}
                placeholder="e.g. pastor-mike"
                data-testid="aff-create-slug"
              />
            </div>
          </Field>
          <button type="submit" disabled={saving}
            className="w-full py-2.5 rounded-xl text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-50"
            style={{ background: colors.accent }}
            data-testid="aff-create-submit"
          >
            {saving ? 'Creating...' : 'Create Affiliate'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AffiliateDetailModal({ colors, isDark, affiliate, detail, loading, onClose, onRefresh, onListRefresh }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [showReferrals, setShowReferrals] = useState(false);
  const [showCommissions, setShowCommissions] = useState(false);
  const [showPayoutRules, setShowPayoutRules] = useState(false);
  const [payoutRules, setPayoutRules] = useState({
    payout_enabled: false, min_subscriber_days: 30, min_payout_amount: 25, max_requests_per_month: 1,
  });
  const [payoutSaving, setPayoutSaving] = useState(false);

  useEffect(() => {
    if (detail) {
      setForm({
        name: detail.name,
        email: detail.email,
        commission_rate: detail.commission_rate,
        commission_type: detail.commission_type,
        commission_duration_months: detail.commission_duration_months || 12,
        is_active: detail.is_active,
        custom_slug: detail.custom_slug || '',
      });
      if (detail.payout_rules) {
        setPayoutRules(detail.payout_rules);
      }
    }
  }, [detail]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/affiliates/${affiliate.affiliate_id}`, form, { withCredentials: true });
      toast.success('Affiliate updated');
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await axios.post(`${API}/admin/affiliates/${affiliate.affiliate_id}/reset-password`, { password: newPw }, { withCredentials: true });
      toast.success('Password reset');
      setShowResetPw(false);
      setNewPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/affiliates/${affiliate.affiliate_id}`, { withCredentials: true });
      toast.success('Affiliate deleted');
      onClose();
      onListRefresh();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await axios.post(`${API}/admin/affiliates/${affiliate.affiliate_id}/mark-paid`, {}, { withCredentials: true });
      toast.success('All pending commissions marked as paid');
      onRefresh();
    } catch {
      toast.error('Failed to mark as paid');
    }
  };

  const handlePayoutRulesSave = async () => {
    setPayoutSaving(true);
    try {
      await axios.put(`${API}/admin/affiliates/${affiliate.affiliate_id}/payout-rules`, payoutRules, { withCredentials: true });
      toast.success('Payout rules updated');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payout rules');
    } finally {
      setPayoutSaving(false);
    }
  };

  const referrals = detail?.referrals || [];
  const commissions = detail?.commissions || [];
  const commTypes = { one_time: 'One-time', time_limited: 'Time-limited', perpetual: 'Perpetual' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-y-auto rounded-2xl border p-6 shadow-2xl"
        style={{
          borderColor: colors.cardBorder,
          background: colors.card,
          maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}>
        <div className="flex items-center justify-between mb-5 sticky top-0 z-10 pb-3" style={{ background: colors.card }}>
          <h2 className="text-lg font-semibold" style={{ color: colors.text }}>
            {detail?.name || affiliate.name}
          </h2>
          <div className="flex items-center gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-white/10 transition" data-testid="edit-affiliate-btn">
                <Edit2 className="h-4 w-4" style={{ color: colors.accent }} />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition">
              <X className="h-4 w-4" style={{ color: colors.textMuted }} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-6 w-6 border-2 border-t-transparent rounded-full" style={{ borderColor: colors.accent }} />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Edit Form or Info Display */}
            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name" colors={colors}>
                    <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                      style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                    />
                  </Field>
                  <Field label="Email" colors={colors}>
                    <input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                      style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Rate (%)" colors={colors}>
                    <input type="number" value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: parseFloat(e.target.value) || 0 }))}
                      min="0" max="100" step="0.1"
                      className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                      style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                    />
                  </Field>
                  <Field label="Type" colors={colors}>
                    <select value={form.commission_type} onChange={e => setForm(f => ({ ...f, commission_type: e.target.value }))}
                      className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                      style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                    >
                      <option value="perpetual">Perpetual</option>
                      <option value="one_time">One-time</option>
                      <option value="time_limited">Time-limited</option>
                    </select>
                  </Field>
                  {form.commission_type === 'time_limited' && (
                    <Field label="Duration (mo)" colors={colors}>
                      <input type="number" value={form.commission_duration_months} onChange={e => setForm(f => ({ ...f, commission_duration_months: parseInt(e.target.value) || 1 }))}
                        min="1" max="120"
                        className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                        style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                      />
                    </Field>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs" style={{ color: colors.textMuted }}>Active</label>
                  <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                    className="p-0.5">
                    {form.is_active
                      ? <ToggleRight className="h-5 w-5 text-emerald-400" />
                      : <ToggleLeft className="h-5 w-5 text-slate-500" />}
                  </button>
                </div>
                <Field label="Custom Vanity Slug" colors={colors}>
                  <div className="flex items-center rounded-xl border overflow-hidden" style={{ borderColor: colors.cardBorder, background: isDark ? 'rgba(255,255,255,0.05)' : '#fff' }}>
                    <span className="text-[10px] pl-3 shrink-0" style={{ color: colors.textMuted }}>/api/r/</span>
                    <input value={form.custom_slug || ''} onChange={e => setForm(f => ({ ...f, custom_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                      className="flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none font-mono"
                      style={{ color: colors.text }}
                      placeholder="e.g. pastor-mike"
                      data-testid="aff-edit-slug"
                    />
                  </div>
                </Field>
                <div className="flex gap-2">
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-50"
                    style={{ background: colors.accent }}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="px-4 py-2 rounded-xl text-sm border transition"
                    style={{ borderColor: colors.cardBorder, color: colors.textMuted }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <InfoItem label="Email" value={detail?.email} colors={colors} />
                <InfoItem label="Ref Code" value={detail?.ref_code} colors={colors} mono />
                <InfoItem label="Rate" value={`${detail?.commission_rate}%`} colors={colors} />
                <InfoItem label="Type" value={commTypes[detail?.commission_type]} colors={colors} />
                {detail?.commission_type === 'time_limited' && (
                  <InfoItem label="Duration" value={`${detail?.commission_duration_months} months`} colors={colors} />
                )}
                <InfoItem label="Status" value={detail?.is_active ? 'Active' : 'Inactive'} colors={colors}
                  valueColor={detail?.is_active ? 'text-emerald-400' : 'text-red-400'} />
                <InfoItem label="Signups" value={detail?.total_signups || 0} colors={colors} />
                <InfoItem label="Conversions" value={detail?.total_conversions || 0} colors={colors} />
                <InfoItem label="Total Earnings" value={`$${(detail?.total_earnings || 0).toFixed(2)}`} colors={colors} />
                <InfoItem label="Pending" value={`$${(detail?.total_pending || 0).toFixed(2)}`} colors={colors} valueColor="text-amber-400" />
                <InfoItem label="Paid" value={`$${(detail?.total_paid || 0).toFixed(2)}`} colors={colors} valueColor="text-emerald-400" />
                <InfoItem label="Joined" value={detail?.created_at ? new Date(detail.created_at).toLocaleDateString() : '-'} colors={colors} />
                {detail?.custom_slug && <InfoItem label="Vanity Link" value={`/api/r/${detail.custom_slug}`} colors={colors} mono />}
                <InfoItem label="2FA" value={detail?.totp_enabled ? 'Enabled' : 'Disabled'} colors={colors}
                  valueColor={detail?.totp_enabled ? 'text-emerald-400' : ''} />
              </div>
            )}

            {/* Action Buttons */}
            {!editing && (
              <div className="flex flex-wrap gap-2">
                <a href={`${API}/admin/affiliates/${affiliate.affiliate_id}/report/pdf`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border transition hover:bg-white/5"
                  style={{ borderColor: colors.cardBorder, color: colors.textMuted }}
                  data-testid="aff-detail-pdf-btn"
                >
                  <FileText className="h-3 w-3" /> PDF Report
                </a>
                <a href={`${API}/admin/affiliates/${affiliate.affiliate_id}/report/csv`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border transition hover:bg-white/5"
                  style={{ borderColor: colors.cardBorder, color: colors.textMuted }}
                  data-testid="aff-detail-csv-btn"
                >
                  <FileSpreadsheet className="h-3 w-3" /> CSV Export
                </a>
                <button onClick={() => setShowResetPw(p => !p)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border transition hover:bg-white/5"
                  style={{ borderColor: colors.cardBorder, color: colors.textMuted }}
                  data-testid="admin-reset-aff-pw-btn"
                >
                  <Lock className="h-3 w-3" /> Reset Password
                </button>
                {(detail?.total_pending || 0) > 0 && (
                  <button onClick={handleMarkPaid}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl text-emerald-400 bg-emerald-400/10 transition hover:bg-emerald-400/20"
                    data-testid="mark-paid-btn"
                  >
                    <DollarSign className="h-3 w-3" /> Mark All Paid
                  </button>
                )}
                <button onClick={handleDelete}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl text-red-400 bg-red-400/10 transition hover:bg-red-400/20 ml-auto"
                  data-testid="delete-affiliate-btn"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            )}

            {/* Reset Password Input */}
            {showResetPw && (
              <div className="flex gap-2">
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                  placeholder="New password (min 8 chars)"
                  className="flex-1 rounded-xl px-3 py-2 text-sm border focus:outline-none"
                  style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                  data-testid="admin-aff-new-pw-input"
                />
                <button onClick={handleResetPassword}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-white transition hover:brightness-110"
                  style={{ background: colors.accent }}>
                  Reset
                </button>
              </div>
            )}

            {/* Referrals Section */}
            <div>
              <button onClick={() => setShowReferrals(p => !p)}
                className="flex items-center gap-2 text-sm font-medium w-full py-2"
                style={{ color: colors.text }}>
                <UserPlus className="h-4 w-4" style={{ color: colors.accent }} />
                Referrals ({referrals.length})
                {showReferrals ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
              </button>
              {showReferrals && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {referrals.length === 0 ? (
                    <p className="text-xs py-2" style={{ color: colors.textMuted }}>No referrals yet</p>
                  ) : referrals.map(r => (
                    <div key={r.referral_id} className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div>
                        <span className="text-xs" style={{ color: colors.text }}>{r.user_name || r.user_email}</span>
                        <span className="text-[10px] ml-2" style={{ color: colors.textMuted }}>{r.user_email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.converted && <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">Converted</span>}
                        <span className="text-[10px]" style={{ color: colors.textMuted }}>{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Commissions Section */}
            <div>
              <button onClick={() => setShowCommissions(p => !p)}
                className="flex items-center gap-2 text-sm font-medium w-full py-2"
                style={{ color: colors.text }}>
                <DollarSign className="h-4 w-4" style={{ color: colors.accent }} />
                Commissions ({commissions.length})
                {showCommissions ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
              </button>
              {showCommissions && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {commissions.length === 0 ? (
                    <p className="text-xs py-2" style={{ color: colors.textMuted }}>No commissions yet</p>
                  ) : commissions.map(c => (
                    <div key={c.commission_id} className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div>
                        <span className="text-xs font-medium" style={{ color: colors.text }}>${c.amount.toFixed(2)}</span>
                        <span className="text-[10px] ml-2" style={{ color: colors.textMuted }}>{c.plan} &middot; {c.commission_rate}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.status === 'paid' ? 'text-emerald-400 bg-emerald-400/10' : 'text-amber-400 bg-amber-400/10'}`}>
                          {c.status}
                        </span>
                        <span className="text-[10px]" style={{ color: colors.textMuted }}>{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payout Rules Configuration */}
            <div>
              <button onClick={() => setShowPayoutRules(p => !p)}
                className="flex items-center gap-2 text-sm font-medium w-full py-2"
                style={{ color: colors.text }}>
                <Settings2 className="h-4 w-4" style={{ color: colors.accent }} />
                Payout Rules
                {showPayoutRules ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
              </button>
              {showPayoutRules && (
                <div className="space-y-3 p-3 rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                  <div className="flex items-center gap-2">
                    <label className="text-xs" style={{ color: colors.textMuted }}>Enable Payout Requests</label>
                    <button onClick={() => setPayoutRules(r => ({ ...r, payout_enabled: !r.payout_enabled }))} className="p-0.5">
                      {payoutRules.payout_enabled
                        ? <ToggleRight className="h-5 w-5 text-emerald-400" />
                        : <ToggleLeft className="h-5 w-5 text-slate-500" />}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Min Subscriber Days" colors={colors}>
                      <input type="number" value={payoutRules.min_subscriber_days} onChange={e => setPayoutRules(r => ({ ...r, min_subscriber_days: parseInt(e.target.value) || 0 }))}
                        min="0" className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                        style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                        data-testid="payout-min-days" />
                    </Field>
                    <Field label="Min Amount ($)" colors={colors}>
                      <input type="number" value={payoutRules.min_payout_amount} onChange={e => setPayoutRules(r => ({ ...r, min_payout_amount: parseFloat(e.target.value) || 0 }))}
                        min="0" step="0.01" className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                        style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                        data-testid="payout-min-amount" />
                    </Field>
                    <Field label="Max Requests/Month" colors={colors}>
                      <input type="number" value={payoutRules.max_requests_per_month} onChange={e => setPayoutRules(r => ({ ...r, max_requests_per_month: parseInt(e.target.value) || 1 }))}
                        min="1" className="w-full rounded-xl px-3 py-2 text-sm border focus:outline-none"
                        style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
                        data-testid="payout-max-requests" />
                    </Field>
                  </div>
                  <button onClick={handlePayoutRulesSave} disabled={payoutSaving}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-white transition hover:brightness-110 disabled:opacity-50"
                    style={{ background: colors.accent }}
                    data-testid="payout-rules-save-btn">
                    {payoutSaving ? 'Saving...' : 'Save Payout Rules'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value, colors, mono, valueColor }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>{label}</p>
      <p className={`text-sm font-medium ${mono ? 'font-mono' : ''} ${valueColor || ''}`} style={valueColor ? {} : { color: colors.text }}>{value ?? '-'}</p>
    </div>
  );
}

function Field({ label, colors, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: colors.textMuted }}>{label}</label>
      {children}
    </div>
  );
}

function ReservedWordsSection({ colors, isDark }) {
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchWords = async () => {
    try {
      const res = await axios.get(`${API}/admin/reserved-words`, { withCredentials: true });
      setWords(res.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchWords(); }, []);

  const addWord = async () => {
    if (!newWord.trim()) return;
    try {
      await axios.post(`${API}/admin/reserved-words`, { word: newWord.toLowerCase().trim() }, { withCredentials: true });
      setNewWord('');
      fetchWords();
      toast.success('Reserved word added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  };

  const removeWord = async (word) => {
    try {
      await axios.delete(`${API}/admin/reserved-words/${word}`, { withCredentials: true });
      fetchWords();
      toast.success('Reserved word removed');
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: colors.cardBorder, background: colors.card }}>
      <button onClick={() => setExpanded(p => !p)} className="flex items-center gap-2 text-sm font-medium w-full" style={{ color: colors.text }}>
        <AlertTriangle className="h-4 w-4" style={{ color: colors.accent }} />
        Reserved Words ({words.length})
        {expanded ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
      </button>
      {expanded && (
        <div className="mt-3 space-y-3">
          <p className="text-xs" style={{ color: colors.textMuted }}>Words that cannot be used as vanity slugs.</p>
          <div className="flex gap-2">
            <input value={newWord} onChange={e => setNewWord(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              className="flex-1 rounded-xl px-3 py-2 text-sm border focus:outline-none font-mono"
              style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderColor: colors.cardBorder, color: colors.text }}
              placeholder="e.g. promo" data-testid="reserved-word-input"
              onKeyDown={e => e.key === 'Enter' && addWord()} />
            <button onClick={addWord} className="px-4 py-2 rounded-xl text-sm font-medium text-white" style={{ background: colors.accent }}
              data-testid="add-reserved-word-btn">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {words.map(w => (
              <span key={w.word} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: colors.text }}>
                {w.word}
                <button onClick={() => removeWord(w.word)} className="hover:text-red-400 transition ml-0.5"><X className="h-3 w-3" /></button>
              </span>
            ))}
            {words.length === 0 && <p className="text-xs" style={{ color: colors.textMuted }}>No custom reserved words</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function PayoutRequestsSection({ colors, isDark }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('pending');

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/payout-requests${filter ? `?status=${filter}` : ''}`, { withCredentials: true });
      setRequests(res.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchRequests(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (requestId, action, notes = '') => {
    try {
      await axios.put(`${API}/admin/payout-requests/${requestId}/${action}`, { notes }, { withCredentials: true });
      toast.success(`Payout request ${action}d`);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action}`); }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: colors.cardBorder, background: colors.card }}>
      <button onClick={() => setExpanded(p => !p)} className="flex items-center gap-2 text-sm font-medium w-full" style={{ color: colors.text }}>
        <Banknote className="h-4 w-4" style={{ color: colors.accent }} />
        Payout Requests {pendingCount > 0 && <span className="text-[10px] bg-amber-400/20 text-amber-400 px-1.5 py-0.5 rounded-full">{pendingCount} pending</span>}
        {expanded ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
      </button>
      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="flex gap-1.5">
            {['pending', 'approved', 'rejected', ''].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                className={`text-[10px] px-2.5 py-1 rounded-lg transition ${filter === s ? 'text-white' : ''}`}
                style={filter === s ? { background: colors.accent } : { color: colors.textMuted, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                {s || 'All'}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {requests.length === 0 ? (
              <p className="text-xs py-3 text-center" style={{ color: colors.textMuted }}>No requests</p>
            ) : requests.map(pr => (
              <div key={pr.request_id} className="p-3 rounded-xl space-y-2" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium" style={{ color: colors.text }}>{pr.affiliate_name}</span>
                    <span className="text-xs ml-2" style={{ color: colors.textMuted }}>{pr.affiliate_email}</span>
                  </div>
                  <span className="text-sm font-bold" style={{ color: colors.text }}>${pr.amount?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>
                    {new Date(pr.created_at).toLocaleDateString()} &middot; {pr.commission_ids?.length || 0} commissions
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    pr.status === 'approved' ? 'text-emerald-400 bg-emerald-400/10' :
                    pr.status === 'rejected' ? 'text-red-400 bg-red-400/10' :
                    'text-amber-400 bg-amber-400/10'
                  }`}>{pr.status}</span>
                </div>
                {pr.status === 'pending' && (
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => handleAction(pr.request_id, 'approve')}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg text-emerald-400 bg-emerald-400/10 hover:bg-emerald-400/20 transition"
                      data-testid={`approve-payout-${pr.request_id}`}>
                      <CheckCircle2 className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={() => { const n = prompt('Rejection reason (optional):'); handleAction(pr.request_id, 'reject', n || ''); }}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg text-red-400 bg-red-400/10 hover:bg-red-400/20 transition"
                      data-testid={`reject-payout-${pr.request_id}`}>
                      <XCircle className="h-3 w-3" /> Reject
                    </button>
                  </div>
                )}
                {pr.admin_notes && <p className="text-[10px] italic" style={{ color: colors.textMuted }}>Note: {pr.admin_notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
